import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SecurityContext, ViewChild, ViewChildren, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { ScriptLoaderService } from './script-loader.service';
import { NanorepService } from './nanorep.service';
import { TranslateService } from '@ngx-translate/core';
import { NanorepAccountInfoModel } from 'common/api/models/nanorep-account-info.model';
import { NanorepAnswer, NanorepResponse, NanorepInterface } from './nanorep-sdk';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { LaneManagerService } from '../lane-buttons/shared/services/lane-manager/lane-manager.service';
import { ChatMessageGroupModel } from '../chat-messages/chat-message-group.model';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { NanorepAnswerComponent } from './nanorep-answer/nanorep-answer.component';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { BcConfigService } from 'common/config/provider';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { Settings } from 'common/settings/settings.interface';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'nanorep-panel',
  templateUrl: './nanorep-panel.component.html',
  styleUrls: ['./nanorep-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class NanorepPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpened: boolean;
  @Input() isExpanded: boolean;
  @Input() workItemId: string;
  @Input() workItemType: WorkItemTypeValue;
  @Input() nanorepConfigId: string;
  @Input() visitor: VisitorInfoModel;

  @Output() panelClose = new EventEmitter<string>();

  @ViewChildren(NanorepAnswerComponent) answerComponents: QueryList<NanorepAnswerComponent>;
  @ViewChild('answerContainer', { static: true }) answerContainer: ElementRef;

  public accountInfo: NanorepAccountInfoModel;
  public hasSearchResult: boolean = true;
  public queryField;
  public answers: { answer: NanorepAnswer, size: number }[] = [];
  public activeAnswerIndex: number;
  public isNavigatingBetweenAnswersEnabled: boolean = false;
  public lastSearchQuery: string;
  public visitorSearchQuery: boolean = false;
  public nextSearchWillBeVisitor: boolean = false;

  private _showUserQueries: boolean = false;
  private nanorepSdkInitiated = false;
  private nanorepScriptLoaded = false;
  private config;
  private _rxEmitterSubscription;

  constructor(private _scriptLoaderService: ScriptLoaderService,
              private _nanorepService: NanorepService,
              private _translateService: TranslateService,
              private bcAnalytics: BcAnalytics,
              private element: ElementRef,
              private rxEmitter: RxEmitter,
              bcConfig: BcConfigService,
              private _settingsService: BcSettingsService,
              private _domSanitizer: DomSanitizer,
              private _changeDetector: ChangeDetectorRef) {
    this.config = bcConfig.current;
  }

  async ngOnInit() {
    this.accountInfo = await this._nanorepService.getAccountInfo();
    this._showUserQueries = await this._isShowUserQueriesEnabled();

    await this._scriptLoaderService.loadScript(this.accountInfo.nanorepHost);
    this.nanorepScriptLoaded = true;
    await this._initNanorepSdkIfParametersSet();

    this.activeAnswerIndex = 0;
  }

  async ngOnDestroy() {
    if (!this._rxEmitterSubscription) {
      return;
    }

    this._rxEmitterSubscription.unsubscribe();
  }

  async ngOnChanges() {
    await this._initNanorepSdkIfParametersSet();
  }

  @HostListener('keydown', ['$event'])
  keyDown(event: KeyboardEvent) {
    if (!this.isNavigatingBetweenAnswersEnabled) {
      return;
    }
    const keyCode = event.keyCode;
    if (keyCode === KeyboardKeyCodes.ENTER) {
      if (this.hasTheSearchQueryChanged || !this.isAnArticleInFocus || this.wasTheEventTriggeredOnResetButton(event)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.answerSelected(this.answers[this.activeAnswerIndex].answer.summary);
    }
    if (keyCode === KeyboardKeyCodes.UP || keyCode === KeyboardKeyCodes.DOWN) {
      event.preventDefault();
      event.stopPropagation();

      if (keyCode === KeyboardKeyCodes.UP) {
        this.activeAnswerIndex--;
        if (this.activeAnswerIndex < 0) {
          this.activeAnswerIndex = this.answers.length - 1;
        }
        this.bcAnalytics.eventTrack('NanorepPanelNavigateUp');
      } else if (keyCode === KeyboardKeyCodes.DOWN) {
        this.activeAnswerIndex++;
        if (this.activeAnswerIndex > this.answers.length - 1) {
          this.activeAnswerIndex = 0;
        }
        this.bcAnalytics.eventTrack('NanorepPanelNavigateDown');
      }

      this.scrollToFocusedElement();
    }
  }

  private scrollToFocusedElement(): void {
    if (!this.answerComponents) {
      return;
    }
    const currentAnswer = this.answerComponents.toArray()[this.activeAnswerIndex];
    if (!currentAnswer) {
      return;
    }

    currentAnswer.scroll();
  }

  private wasTheEventTriggeredOnResetButton(event: KeyboardEvent): boolean {
    return (<Element>event.target).className.includes('query-field__button--reset');
  }

  private get hasTheSearchQueryChanged(): boolean {
    if (!this.queryField.value) {
      // Note: We don't want to handle events on empty query field
      return false;
    }

    return this.queryField.value !== this.lastSearchQuery;
  }

  private get isAnArticleInFocus(): boolean {
    return this.activeAnswerIndex >= 0;
  }

  closeNanorepPanel() {
    this.panelClose.emit(this.workItemId);
    this.bcAnalytics.eventTrack('NanorepPanelClosed');
  }

  private async _doSearch(query) {
    const context = this._isContextualSearchEnabled() ? this._nanorepService.buildContext(this.visitor) : null;
    if (window.nanorep.api.setSearchSession) {
      await window.nanorep.api.setSearchSession({
        host: this.accountInfo.nanorepHost,
        account: this.accountInfo.nanorepAccountName,
        forceConfigId: this.nanorepConfigId,
        context: context ? context.nrContext : null,
        preview: !this._showUserQueries // Preview config property mutes voices and feedback on Nanorep side if true
      });
    }
    window.nanorep.api.search.search({ text: query }).then((response: NanorepResponse) => {
      if (!this.nextSearchWillBeVisitor) {
        this.visitorSearchQuery = false;
      }
      this.nextSearchWillBeVisitor = false;

      const containerPadding = 82;
      const minHeight = 100;
      const maxSize = this.answerContainer.nativeElement.offsetHeight - containerPadding;
      let maxCharacters = 0;
      let maxSizeOffset = 0;
      response.answers.forEach(answer => {
        answer.summary = this._domSanitizer.sanitize(SecurityContext.HTML, answer.summary);
        maxCharacters += answer.summary.length;
      });
      response.answers.forEach(answer => {
        maxSizeOffset += Math.max(minHeight - this.calculateAnswersHeight(answer, maxCharacters, maxSize), 0);
      });

      const maxSizeWithOffset = maxSize - maxSizeOffset;
      this.answers = response.answers.map((answer, index) => {
        return {
          answer,
          size: this.calculateAnswersHeight(answer, maxCharacters, maxSize) < minHeight ? minHeight : this.calculateAnswersHeight(answer, maxCharacters, maxSizeWithOffset),
          index
        };
      });
      this.hasSearchResult = !!response.answers.length;
      this.isNavigatingBetweenAnswersEnabled = true;
      this.lastSearchQuery = query;

      const eventParams = {};
      if (response.answers.length || !this.visitorSearchQuery) {
        eventParams['result count'] = response.answers.length;
      }
      this._changeDetector.detectChanges();
      this.bcAnalytics.eventTrack('NanorepPanelManualSearch', eventParams);
    });
  }

  private calculateAnswersHeight(answer: NanorepAnswer, maxCharacters: number, maxSize: number): number {
    return Math.floor(answer.summary.length / maxCharacters * maxSize);
  }

  private async _buildQueryField() {
    const translatedInputPlaceholderText = await this._translateService.get('nanorepPanel.searchInputPlaceholder').toPromise();
    this.queryField = new window.nanorep.sdk.QueryField({
      initialText: translatedInputPlaceholderText,
      showSearchButton: true,
      autocompleteEnabled: true
    }, this.element.nativeElement.querySelector('.query-field-container'));
    this.queryField.on({
      search: (query) => {
        this._doSearch(query);
      },
      newSuggestions: () => {
        this.isNavigatingBetweenAnswersEnabled = false;
      },
      selectSuggestion: (suggestionQuery) => {
        this._doSearch(suggestionQuery);
      },
      reset: () => {
        this.answers = [];
        this.hasSearchResult = true;
      }
    });
  }

  private async _initNanorepSdkIfParametersSet(): Promise<void> {
    if (!this.nanorepSdkInitiated && this._isNanorepSdkReadyToBeInited()) {
      this.nanorepSdkInitiated = true;
      const context = this._isContextualSearchEnabled() ? this._nanorepService.buildContext(this.visitor) : null;
      await this._loadNanorepData(this.accountInfo.nanorepAccountName, this.accountInfo.nanorepHost, this.nanorepConfigId, context);
      this._rxEmitterSubscription = this.rxEmitter.subscribe(LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.SendWorkItemContent, this.workItemId), (message: ChatMessageGroupModel[]) => this._onMessage(message));
    }
  }

  private async _loadNanorepData(account: string, host: string, nanorepConfigId: string, context: any) {
    if (window.nanorep.api.setSearchSession) {
      window.nanorep.api.setSearchSession({
        host: host,
        account: account,
        forceConfigId: nanorepConfigId,
        context: context ? context.nrContext : null,
        preview: !this._showUserQueries // Preview config property mutes voices and feedback on Nanorep side if true
      });
    } else {
      window.nanorep.sdk.init({
        host: host,
        account: account,
        forceConfigId: nanorepConfigId,
        context: null, // Context is not supported in old Nanorep SDK (before 18 Jun 2018, version: 4.18.1.5)
        preview: !this._showUserQueries // Preview config property mutes voices and feedback on Nanorep side if true
      });
    }

    this.bcAnalytics.eventTrack('NanorepPanelIsLoaded');
    await this._buildQueryField();
  }

  private _onMessage(message: ChatMessageGroupModel[]) {
    if (!this.queryField.value) {
      const lastMessageGroup = message[message.length - 1];
      if (lastMessageGroup.type === bcEnums.messageTypes.VISITOR) {
        this.visitorSearchQuery = true;
        this.nextSearchWillBeVisitor = true;
        const lastVisitorMessageText = this._buildSearchQueryFromLastVisitorMessages(lastMessageGroup);
        this._setQueryFieldValue(lastVisitorMessageText);
      }
    }
  }

  answerSelected(article: string): void {
    this.rxEmitter.send(LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.SendCannedMessageToEditor, this.workItemId), article);
    this.activeAnswerIndex = -1;
    this.bcAnalytics.eventTrack('NanorepPanelAnswerSelected');
  }

  answerFocused(index: number) {
    if (!this.isNavigatingBetweenAnswersEnabled) {
      return;
    }

    this.activeAnswerIndex = index;
  }

  private _setQueryFieldValue(query) {
    this.queryField.emit('search', query);
  }

  private _buildSearchQueryFromLastVisitorMessages(lastVisitorMessageGroup: ChatMessageGroupModel) {
    const lastMessageGroupMessages = lastVisitorMessageGroup.messages;
    return lastMessageGroupMessages.map((message) => message.Text).join(' ');
  }

  private _isNanorepSdkReadyToBeInited(): boolean {
    return this.nanorepScriptLoaded && this.nanorepConfigId && this._isVisitorInfoLoaded();
  }

  private _isVisitorInfoLoaded(): boolean {
    return !!(this.visitor && this.visitor.fields && this.visitor.fields.email &&
      (this.workItemType !== WorkItemTypeValue.CHAT || this.visitor.fields.customPreChatFields));
  }

  private _isContextualSearchEnabled(): boolean {
    return !!this.config.features.contextualKnowledgeBaseSearch;
  }

  private async _isShowUserQueriesEnabled(): Promise<boolean> {
    const settings: Settings = await this._settingsService.getSettings();
    return this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_SHOW_USER_QUERIES_NANOREP);
  }
}

declare global {
  interface Window {
    nanorep: NanorepInterface;
  }
}
